// frontend/src/pages/reportpage/LoansReportTab.jsx
import React from 'react';
import { exportToCSV, formatDayMonthYear } from './reportUtils';

// Soft, muted background colors for grouping rows by month (cycled, non-shouting)
const MONTH_HIGHLIGHT_COLORS = [
  '#eef2ff', // soft indigo
  '#ecfdf5', // soft green
  '#fff7ed', // soft orange
  '#f0f9ff', // soft sky blue
  '#fdf4ff', // soft purple
  '#fefce8', // soft yellow
  '#f0fdfa', // soft teal
  '#fef2f2', // soft rose
];

const getMonthKey = (dateStr) => {
  if (!dateStr) return 'unknown';
  const parts = String(dateStr).split('-');
  if (parts.length < 2) return 'unknown';
  return `${parts[0]}-${parts[1]}`;
};

// Builds a stable month -> color map, assigned in the order months first
// appear in the full (unpaginated) loan list, so colors don't shift between pages.
const buildMonthColorMap = (loans) => {
  const map = {};
  let colorIndex = 0;
  (loans || []).forEach((loan) => {
    const key = getMonthKey(loan.applicationDate);
    if (!(key in map)) {
      map[key] = MONTH_HIGHLIGHT_COLORS[colorIndex % MONTH_HIGHLIGHT_COLORS.length];
      colorIndex += 1;
    }
  });
  return map;
};

const LoansReportTab = ({
  isLoading,
  loansReport,
  loansPage,
  setLoansPage,
  loansPageSize,
  setLoansPageSize,
}) => {
  const totalLoans = loansReport ? loansReport.loans.length : 0;
  const isAll = loansPageSize === 'all';
  const totalPages = isAll ? 1 : Math.max(1, Math.ceil(totalLoans / loansPageSize));
  const visibleLoans = loansReport
    ? (isAll ? loansReport.loans : loansReport.loans.slice((loansPage - 1) * loansPageSize, loansPage * loansPageSize))
    : [];
  const monthColorMap = loansReport ? buildMonthColorMap(loansReport.loans) : {};

  const handlePageSizeChange = (e) => {
    const val = e.target.value === 'all' ? 'all' : Number(e.target.value);
    setLoansPageSize(val);
    setLoansPage(1);
  };

  return (
    <div className="loans-tab">
      {isLoading ? (
        <div className="loading">Loading loans report...</div>
      ) : loansReport ? (
        <div className="report-content">
          <div className="report-title-row">
            <h3>Loans Report</h3>
            <div className="btn-group">
              <button className="btn btn-outline btn-sm" onClick={() => window.print()}>
                🖨️ Print
              </button>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => exportToCSV(loansReport.loans, `loans-report.csv`)}
              >
                ⤓ Export CSV
              </button>
            </div>
          </div>

          <div className="compact-summary">
            <div className="compact-summary-item">
              <span className="compact-label">Total:</span>
              <span className="compact-value">{loansReport.summary.totalLoans}</span>
            </div>
            <div className="compact-summary-item">
              <span className="compact-label">Active:</span>
              <span className="compact-value">{loansReport.summary.totalActive}</span>
            </div>
            <div className="compact-summary-item">
              <span className="compact-label">Completed:</span>
              <span className="compact-value">{loansReport.summary.totalCompleted}</span>
            </div>
            <div className="compact-summary-item">
              <span className="compact-label">Disbursed:</span>
              <span className="compact-value">KES {loansReport.summary.totalDisbursed?.toLocaleString()}</span>
            </div>
            <div className="compact-summary-item highlight">
              <span className="compact-label">Outstanding:</span>
              <span className="compact-value text-danger">KES {loansReport.summary.totalOutstanding?.toLocaleString()}</span>
            </div>
          </div>

          <div className="compact-summary">
            <span className="compact-section-label">By Type:</span>
            <div className="compact-summary-item">
              <span className="compact-label">Normal:</span>
              <span className="compact-value">{loansReport.summary.byType.normal}</span>
            </div>
            <div className="compact-summary-item">
              <span className="compact-label">School:</span>
              <span className="compact-value">{loansReport.summary.byType.school}</span>
            </div>
            <div className="compact-summary-item">
              <span className="compact-label">Emergency:</span>
              <span className="compact-value">{loansReport.summary.byType.emergency}</span>
            </div>
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Loan ID</th>
                  <th>Applied</th>
                  <th>Member</th>
                  <th>Type</th>
                  <th>Original Amount</th>
                  <th>Balance</th>
                  <th>Monthly</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {visibleLoans.map((loan) => (
                  <tr key={loan.id} style={{ backgroundColor: monthColorMap[getMonthKey(loan.applicationDate)] }}>
                    <td>{loan.loanId}</td>
                    <td>{formatDayMonthYear(loan.applicationDate)}</td>
                    <td>{loan.memberName}</td>
                    <td>
                      <span className={`loan-type ${loan.loanType}`}>{loan.loanType}</span>
                    </td>
                    <td>KES {loan.originalAmount?.toLocaleString()}</td>
                    <td>KES {loan.currentBalance?.toLocaleString()}</td>
                    <td>KES {loan.totalMonthlyDeduction?.toLocaleString()}</td>
                    <td>
                      <span className={`status-badge ${loan.status}`}>{loan.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div
              className="table-note"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                padding: '12px 0',
              }}
            >
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                Show:
                <select
                  value={loansPageSize}
                  onChange={handlePageSizeChange}
                  style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #ccc' }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value="all">All</option>
                </select>
              </label>

              {!isAll && totalPages > 1 && (
                <>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => setLoansPage((p) => Math.max(1, p - 1))}
                    disabled={loansPage === 1}
                  >
                    ← Prev
                  </button>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                      <button
                        key={pageNum}
                        className={`btn btn-sm ${pageNum === loansPage ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => setLoansPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    ))}
                  </div>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => setLoansPage((p) => Math.min(totalPages, p + 1))}
                    disabled={loansPage === totalPages}
                  >
                    Next →
                  </button>
                </>
              )}

              <span>
                {isAll
                  ? `Showing all ${totalLoans} loans`
                  : `Showing ${Math.min((loansPage - 1) * loansPageSize + 1, totalLoans)}–${Math.min(loansPage * loansPageSize, totalLoans)} of ${totalLoans} loans`}
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LoansReportTab;
